import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface Section {
  id: string;
  title: string;
}

const SECTIONS: Section[] = [
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'dashboard', title: 'Dashboard' },
  { id: 'baby-profiles', title: 'Baby Profiles' },
  { id: 'pregnancy-profiles', title: 'Pregnancy Tracking' },
  { id: 'tracking-events', title: 'Tracking Events' },
  { id: 'timers', title: 'Timers' },
  { id: 'health-metrics', title: 'Health Metrics' },
  { id: 'sharing-caregivers', title: 'Sharing & Caregivers' },
  { id: 'emergency-access', title: 'Emergency Access' },
  { id: 'share-links', title: 'Share Links' },
  { id: 'reminders', title: 'Reminders' },
  { id: 'support-tickets', title: 'Support Tickets' },
  { id: 'data-export', title: 'Data Export' },
  { id: 'privacy-security', title: 'Privacy & Security' },
  { id: 'settings', title: 'Settings' },
  { id: 'push-notifications', title: 'Push Notifications' },
  { id: 'offline-mode', title: 'Offline Mode' },
  { id: 'installing-app', title: 'Installing the App' },
];

export default function Documentation() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [tocOpen, setTocOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleSectionClick = useCallback((sectionId: string) => {
    setTocOpen(false);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    const headings = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    headings.forEach((el) => observerRef.current!.observe(el!));

    return () => observerRef.current?.disconnect();
  }, []);

  const sidebarContent = (
    <nav className="space-y-1">
      {SECTIONS.map((section) => (
        <button
          key={section.id}
          onClick={() => handleSectionClick(section.id)}
          className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
            activeSection === section.id
              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          {section.title}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Link
        to="/app/support"
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6"
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Support
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
        Documentation
      </h1>

      {/* Mobile TOC toggle */}
      <div className="md:hidden mb-6">
        <button
          onClick={() => setTocOpen(!tocOpen)}
          className="w-full flex items-center justify-between px-4 py-3 card"
        >
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            Table of Contents
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${tocOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {tocOpen && (
          <div className="card mt-2 p-4">
            {sidebarContent}
          </div>
        )}
      </div>

      <div className="flex gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            {sidebarContent}
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 space-y-8">

          {/* Getting Started */}
          <section id="getting-started" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Getting Started</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Creating Your Account</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Visit the registration page and enter your name, email address, and a strong password.</li>
                <li>Your password must be at least 12 characters and include uppercase, lowercase, a number, and a special character.</li>
                <li>When you register, encryption keys are generated on your device. Your password is used to protect these keys — it is never sent to our server.</li>
                <li>Check your email for a verification link and click it to activate your account.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">First Login</h3>
              <p>After verifying your email, log in with your credentials. You'll be taken to an empty Dashboard — your starting point for tracking your baby's day.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Adding Your First Baby</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>On the Dashboard, tap <strong>"Add Baby"</strong>.</li>
                <li>Enter your baby's name, date of birth, and gender.</li>
                <li>Optionally add medical information such as blood type, allergies, and pediatrician details.</li>
                <li>All of this information is encrypted on your device before being saved.</li>
              </ol>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm"><strong>Tip:</strong> You can track multiple babies — just tap "Add Baby" again. Switch between babies using the baby selector on the Dashboard.</p>
              </div>
            </div>
          </section>

          {/* Dashboard */}
          <section id="dashboard" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Dashboard</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>The Dashboard is your home screen and shows an overview of your baby's day at a glance.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Baby Selector</h3>
              <p>If you have multiple babies, use the selector at the top of the Dashboard to switch between them. The entire Dashboard updates to show the selected baby's data.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Daily Summary</h3>
              <p>See today's event counts at a glance — how many feeds, nappy changes, and sleep sessions have been logged. Totals update in real time as you or other caregivers log events.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Quick Log Buttons</h3>
              <p>Tap any event type button (Feed, Nappy, Sleep, etc.) to quickly log an event with a single tap. You can also start timers for feeding and sleep from these buttons.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Active Timers</h3>
              <p>Any running timers appear on the Dashboard. You can see elapsed time, pause/resume, switch sides (for feeding), or stop and log the event.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Recent Events</h3>
              <p>A timeline of the most recent events appears below the summary. Tap any event to view details or edit it.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Quick Links</h3>
              <p>At the bottom of the Dashboard you'll find links to <strong>View All History</strong> (full event timeline) and <strong>Health Metrics</strong> (growth charts and feeding pattern analysis).</p>
            </div>
          </section>

          {/* Baby Profiles */}
          <section id="baby-profiles" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Baby Profiles</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Each baby has a profile page where you manage their information, caregivers, and sharing settings. You can also create <strong>pregnancy profiles</strong> — see the Pregnancy Tracking section below.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Adding a Profile</h3>
              <p>Tap <strong>"Add"</strong> from the Dashboard. You'll be asked what you'd like to track — choose <strong>Baby</strong> for a newborn/infant profile, or <strong>Pregnancy</strong> to start tracking your pregnancy journey. All profile information is encrypted on your device before being saved.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Profile Information</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Name and date of birth</strong> — used for age calculations and display.</li>
                <li><strong>Gender</strong> — used for growth chart percentiles.</li>
                <li><strong>Profile photo</strong> — encrypted and stored securely. Tap the photo area to upload or change.</li>
                <li><strong>Medical info</strong> — blood type, allergies, medications, and notes.</li>
                <li><strong>Pediatrician</strong> — name, phone number, and address for quick reference.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Editing a Profile</h3>
              <p>Tap the edit button on any section to modify it. Changes are encrypted and saved automatically. All caregivers with access will see the updated information.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Feed Settings</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Daily feed target</strong> — set a target in mL to track progress on the Dashboard.</li>
                <li><strong>Post-feed upright timer</strong> — configure a countdown timer that prompts after each feed to keep baby upright (helps prevent spit-up).</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Deleting a Baby</h3>
              <p>Only the primary caregiver can delete a baby profile. This permanently removes all events, photos, and shared access. A confirmation dialog prevents accidental deletion.</p>
            </div>
          </section>

          {/* Pregnancy Profiles */}
          <section id="pregnancy-profiles" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Pregnancy Tracking</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>myTotTot supports pregnancy tracking from the moment you find out. When adding a new profile, choose "Pregnancy" to get started.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Setting Up a Pregnancy Profile</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>From the Dashboard, tap <strong>"Add"</strong>.</li>
                <li>Choose <strong>"Pregnancy"</strong> from the options.</li>
                <li>Enter your due date — the app calculates your current gestational week and estimated date of conception.</li>
                <li>Optionally enter your pre-pregnancy weight, OB/GYN information, and relevant medical details.</li>
                <li>All information is encrypted on your device before being saved.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Dashboard for Pregnancy</h3>
              <p>The pregnancy Dashboard shows your current gestational week with a coloured progress bar (pink gradient, 0-40 weeks), along with quick links to track pregnancy events. You'll see a weekly summary of symptoms, kicks, contractions, and appointments.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Pregnancy Timeline</h3>
              <p>Tap the <strong>"Timeline"</strong> tab from your pregnancy Dashboard to see all events grouped by gestational week. The timeline includes a weight gain chart so you can monitor changes throughout your pregnancy. This is great for reviewing with your midwife or doctor.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Pregnancy Health Metrics</h3>
              <p>The Health Metrics page for pregnancy profiles includes charts for weight gain, blood pressure trends, and kick count patterns. All charts support the same 7/14/30 day period selector as baby charts.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Converting to a Baby Profile</h3>
              <p>When your baby arrives, go to your pregnancy profile settings and tap <strong>"Convert to Baby Profile"</strong>. Enter your baby's name, gender, and date of birth. The conversion:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Is <strong>one-way</strong> — the pregnancy profile becomes a baby profile.</li>
                <li><strong>Preserves all history</strong> — your full pregnancy timeline and events stay with you.</li>
                <li>Is <strong>instant</strong> — no data export or account switching needed.</li>
                <li>Changes the Dashboard from pregnancy view to baby view with all standard baby tracking features.</li>
              </ul>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm"><strong>Tip:</strong> You can have both a pregnancy profile (for a current pregnancy) and baby profiles (for older children) at the same time. Switch between them using the profile selector on the Dashboard.</p>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border-l-4 border-amber-500">
                <p className="text-sm"><strong>⚠️ Medical Disclaimer:</strong> myTotTot is a tracking tool, not a medical device. It does not provide medical advice, diagnosis, or treatment. Always consult your healthcare provider for any concerns about your pregnancy or baby's health.</p>
              </div>
            </div>
          </section>

          {/* Tracking Events */}
          <section id="tracking-events" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Tracking Events</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Events are the core of myTotTot. Every feed, nappy change, sleep session, and more is logged as an event.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">How to Log an Event</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>From the Dashboard, tap the quick log button for the event type, or tap the "+" button to open the full event form.</li>
                <li>Select the event type, fill in the details, and add optional notes or photos.</li>
                <li>The timestamp defaults to now but can be adjusted if you're logging retroactively.</li>
                <li>Tap "Save" to encrypt and store the event.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Event Types (Baby)</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Feed</strong> — Breastfeeding with side tracking (left/right/both), duration, and optional notes.</li>
                <li><strong>Bottle</strong> — Formula or expressed breast milk. Track brand, amount offered and consumed.</li>
                <li><strong>Nappy</strong> — Wet, dirty, or both. Track diaper rash presence and severity.</li>
                <li><strong>Sleep</strong> — Nap or night sleep with duration and location.</li>
                <li><strong>Spit Up</strong> — Severity levels: a little, some, or lots.</li>
                <li><strong>Medication</strong> — Name, dose, and next due time for medication tracking.</li>
                <li><strong>Weight</strong> — Weight in kg or lbs. Also supports height and head circumference.</li>
                <li><strong>Temperature</strong> — Temperature in Celsius or Fahrenheit with measurement method.</li>
                <li><strong>Tummy Time</strong> — Duration and head-up time in MM:SS format. Use the dedicated tummy timer for hands-free tracking.</li>
                <li><strong>Pump</strong> — Breast pump sessions with left/right amounts and storage location.</li>
                <li><strong>EC (Elimination Communication)</strong> — Track pee and poop outcomes for EC sessions, with three levels: none, a little, or loads. View patterns over time in Health Metrics.</li>
                <li><strong>Journal</strong> — Free-form entries for milestones, memories, and notes. Add tags and mark as milestones.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Event Types (Pregnancy)</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Symptom</strong> — Track 24 different pregnancy symptoms (nausea, back pain, heartburn, insomnia, and more) with severity ratings: mild, moderate, or severe.</li>
                <li><strong>Appointment</strong> — Log prenatal checkups, ultrasounds, blood tests, and other appointments with provider details.</li>
                <li><strong>Blood Pressure</strong> — Record systolic and diastolic readings to monitor trends throughout pregnancy.</li>
                <li><strong>Ultrasound</strong> — Document ultrasound scans with notes and findings.</li>
                <li><strong>Kick Count</strong> — Log fetal movement sessions with kick count totals to track baby's activity patterns.</li>
                <li><strong>Contraction</strong> — Time and track contractions leading up to labour.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Editing and Deleting Events</h3>
              <p>Tap any event in the history to view its details. From there you can edit any field or delete the event entirely. Deletions are permanent and sync across all caregivers.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Photo Attachments</h3>
              <p>You can attach up to 2 photos per event. Photos are encrypted on your device before upload and stored in secure cloud storage. Only caregivers with access can view them.</p>
            </div>
          </section>

          {/* Timers */}
          <section id="timers" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Timers</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Timers let you track feeding and sleep sessions in real time without watching the clock.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Starting a Timer</h3>
              <p>On the Dashboard, tap the <strong>Feed</strong> or <strong>Sleep</strong> button. The timer starts immediately and appears on the Dashboard with a live elapsed time display.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Pause and Resume</h3>
              <p>Tap the pause button to temporarily stop the timer (e.g., for a burp break). The timer freezes and shows the paused duration. Tap resume to continue tracking.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Switching Sides (Breastfeeding)</h3>
              <p>For breastfeeding, tap the side switch button to toggle between left and right. The timer tracks each side switch so you know how long was spent on each side.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Stopping and Logging</h3>
              <p>When you're done, tap <strong>"Stop & Log"</strong>. The event form opens automatically with the duration pre-filled. Review the details and tap "Save".</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Post-Feed Upright Timer</h3>
              <p>If configured in baby settings, a countdown timer automatically prompts after logging a feed. This helps you track how long to keep baby upright. The timer counts down and notifies you when the time is up.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Tummy Time Timer</h3>
              <p>Tap the <strong>🏋️ Tummy</strong> button on the Dashboard to start a dedicated tummy time timer. This timer includes a <strong>Head Up / Head Down</strong> toggle — tap it when your baby lifts their head and again when they rest. The timer shows both total elapsed time and head-up time simultaneously. When you tap "Stop & Log", both durations are pre-filled in the event form. Tummy time uses MM:SS format since sessions are typically under an hour.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Real-Time Sync</h3>
              <p>Timers sync across all caregivers' devices in real time via WebSocket. If one caregiver starts a timer, everyone sees it on their Dashboard.</p>
            </div>
          </section>

          {/* Health Metrics */}
          <section id="health-metrics" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Health Metrics</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Track your baby's growth and feeding patterns with interactive charts. Access Health Metrics from the <strong>"Health Metrics →"</strong> link at the bottom of the Dashboard.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Growth Charts</h3>
              <p>Weight, height, and head circumference measurements are plotted on interactive charts. View trends over time and compare against standard growth percentiles.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">WHO Percentile Curves</h3>
              <p>Growth charts overlay WHO percentile curves (3rd, 15th, 50th, 85th, 97th) based on your baby's gender. This helps you and your pediatrician quickly assess growth patterns.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Feeding Patterns</h3>
              <p>The Feeding Patterns section shows two interactive charts that help you understand your baby's feeding habits:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Feed Times Chart</strong> — A scatter plot showing when feeds happen throughout the day. Each dot represents a feed, plotted by time of day (horizontal) and date (vertical). Breastfeeds appear in cyan and formula/bottle feeds in purple. Larger dots indicate higher volumes.</li>
                <li><strong>Average Volume by Hour</strong> — A bar chart showing how much your baby typically consumes at different times of day. Helps you spot patterns like larger morning feeds or cluster feeding in the evening.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Period Selection</h3>
              <p>Toggle between <strong>7 days</strong>, <strong>14 days</strong>, or <strong>30 days</strong> to adjust the time range for feeding pattern charts. A longer period reveals more trends but may take slightly longer to load as more events are decrypted.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Medication Frequency</h3>
              <p>The Medication Frequency section appears below Feeding Patterns and shows two charts:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Medication Scatter Plot</strong> — Each dose shown as a dot by time of day and date, colour-coded by medication name using an 8-colour palette. Helps you see timing patterns for different medications.</li>
                <li><strong>Daily Stacked Bar Chart</strong> — Shows how many doses of each medication were given each day, stacked by medication. Useful for tracking multi-medication schedules.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">EC Patterns</h3>
              <p>The EC (Elimination Communication) section shows a stacked bar chart of daily pee and poop outcomes, broken down by quantity: "loads" and "a little". A summary displays total sessions, pee catches, and poop catches for the selected period. "None" outcomes are counted in the stats but not shown in the chart.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Tummy Time Progress</h3>
              <p>The Tummy Time section features a line chart with two lines per session: total tummy time and head-up time (both in seconds, displayed as MM:SS). A summary shows session count, total tummy minutes, and average head-up percentage — great for tracking developmental progress.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Pregnancy Metrics</h3>
              <p>For pregnancy profiles, Health Metrics includes pregnancy-specific charts: a <strong>Weight Gain</strong> chart tracking weight throughout pregnancy, a <strong>Blood Pressure</strong> chart showing systolic and diastolic trends, and a <strong>Kick Count</strong> chart displaying fetal movement patterns. These are particularly useful to share with your midwife or doctor during prenatal checkups.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Logging Measurements</h3>
              <p>Log weight, height, and head circumference from the "Weight" event type. Select your preferred units (kg/lbs, cm/inches) and the app automatically handles conversions for charts.</p>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm"><strong>Tip:</strong> Log measurements regularly (weekly for weight, monthly for height) to get the most useful growth trends. Feed and bottle events are automatically included in the feeding pattern charts — no extra steps needed. For pregnancy, log your weight at each prenatal visit to build a comprehensive weight gain chart.</p>
              </div>
            </div>
          </section>

          {/* Support Tickets */}
          <section id="support-tickets" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Support Tickets</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>If you run into a problem or have an idea for a new feature, you can raise a support ticket directly from within the app.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Creating a Ticket</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to <strong>Help & Support</strong> and tap <strong>"Raise a Ticket"</strong>.</li>
                <li>Choose the type: <strong>Problem</strong> (something isn't working) or <strong>Feature Request</strong> (you'd like something new).</li>
                <li>For problems, set a priority level (Low, Normal, High, or Urgent).</li>
                <li>Enter a subject and description of your issue.</li>
                <li>Optionally attach up to 3 screenshots (JPEG, PNG, GIF, or WebP, max 5MB each).</li>
                <li>Tap "Submit Ticket" — your ticket and any attachments are encrypted before being sent.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Tracking Your Tickets</h3>
              <p>All your tickets are listed below the creation form. Tap a ticket to see its full conversation, including any replies from the support team. Tickets move through statuses: <strong>Open → In Progress → Resolved → Closed</strong>.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Replying to Tickets</h3>
              <p>You can reply to open tickets and attach additional screenshots. Replies are encrypted end-to-end, just like the original ticket.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Support Access Codes</h3>
              <p>If an admin needs to look at your account metadata to help troubleshoot, you can generate a <strong>6-digit access code</strong> from within the ticket. This code is single-use, expires in 24 hours, and only grants access to non-encrypted account information (like your account creation date and settings) — not your baby's data.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Encrypted Attachments</h3>
              <p>Screenshots you attach to tickets are encrypted on your device before upload, just like all other data in myTotTot. Only you and the admin team can decrypt and view them.</p>
            </div>
          </section>

          {/* Sharing & Caregivers */}
          <section id="sharing-caregivers" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Sharing & Caregivers</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Invite your partner, family members, or nanny to help track your baby. Everyone stays in sync.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Inviting a Caregiver</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to your baby's profile page.</li>
                <li>Tap <strong>"Invite Caregiver"</strong>.</li>
                <li>Optionally enter their email address.</li>
                <li>Choose a role: <strong>Primary</strong> (full admin access) or <strong>Member</strong> (can log events).</li>
                <li>A secure invite link is generated. Share it with them via text, email, or any messaging app.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Accepting an Invite</h3>
              <p>When someone opens the invite link, they'll be asked to log in or create an account. The baby's encryption key is securely transferred to their account, giving them access to all data.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Roles</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Primary</strong> — Full access. Can edit baby profile, manage caregivers, create share links, and delete the baby.</li>
                <li><strong>Member</strong> — Can view all data, log events, and use timers. Cannot manage caregivers or delete the baby.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Removing Access</h3>
              <p>Primary caregivers can remove other members from the baby's profile page. The removed user immediately loses access to all data.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Real-Time Sync</h3>
              <p>All events and timers sync across caregivers in real time. When one person logs a feed, everyone's Dashboard updates within seconds.</p>
            </div>
          </section>

          {/* Emergency Access */}
          <section id="emergency-access" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Emergency Access</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Designate trusted contacts who can request temporary access to your baby's health information in an emergency.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Setting Up Emergency Contacts</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to your baby's profile page.</li>
                <li>Scroll to the <strong>"Emergency Contacts"</strong> section.</li>
                <li>Tap "Add Contact" and enter their name, email, and relationship.</li>
                <li>An emergency access packet is created containing essential health information.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">How Contacts Request Access</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Your contact visits the Emergency Access page (linked from the login screen).</li>
                <li>They enter their email address to look up their access.</li>
                <li>They submit an access request with an optional reason.</li>
                <li>You receive a notification and can approve or deny the request.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Time-Limited Access</h3>
              <p>When you approve a request, access is granted for a limited time (24, 48, or 72 hours). The contact can view basic health information during this window. Access automatically expires when the time is up.</p>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm"><strong>Tip:</strong> The emergency access interface is designed for elderly users with large text and simple navigation. Share the direct link with your contacts so they can bookmark it.</p>
              </div>
            </div>
          </section>

          {/* Share Links */}
          <section id="share-links" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Share Links</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Create read-only links to share specific data with pediatricians, family members, or anyone who doesn't need an account.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Creating a Share Link</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to your baby's profile page.</li>
                <li>Tap <strong>"Share Link"</strong>.</li>
                <li>Choose which event types to include (or select all).</li>
                <li>Optionally set a date range to limit the data shown.</li>
                <li>Set an expiry (1-30 days) and optional access limit.</li>
                <li>Add an optional recipient name and email for your records.</li>
                <li>Copy the generated link and send it to your recipient.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">What Recipients See</h3>
              <p>Recipients see a read-only view of events matching your settings. They cannot edit, add, or delete anything. No account is required — the link contains everything needed to decrypt the shared data.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Revoking a Share Link</h3>
              <p>You can revoke any share link at any time from the baby's profile page. Once revoked, the link immediately stops working, even if it hasn't expired yet.</p>
            </div>
          </section>

          {/* Reminders */}
          <section id="reminders" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Reminders</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Set reminders for feeding schedules, medication times, appointments, or anything else you don't want to forget.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Creating a Reminder</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to your baby's Reminders page (from the Dashboard menu).</li>
                <li>Tap <strong>"New Reminder"</strong>.</li>
                <li>Select a type: Medication, Appointment, or Custom.</li>
                <li>Enter a message describing what the reminder is for.</li>
                <li>Set the date and time for the reminder.</li>
                <li>Optionally set recurrence: daily, weekly, or monthly.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Recurrence Options</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>None</strong> — One-time reminder.</li>
                <li><strong>Daily</strong> — Repeats every day (or every N days).</li>
                <li><strong>Weekly</strong> — Repeats every week (or every N weeks).</li>
                <li><strong>Monthly</strong> — Repeats every month (or every N months).</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Managing Reminders</h3>
              <p>View all reminders in a list. Edit the time, message, or recurrence. Dismiss reminders you no longer need. Delete reminders permanently.</p>
            </div>
          </section>

          {/* Data Export */}
          <section id="data-export" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Export</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>Export your baby's data as a PDF report or CSV spreadsheet for your records or to share with your pediatrician.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">How to Export</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to <strong>Settings</strong> and tap <strong>"Export Data"</strong>.</li>
                <li>Select the baby whose data you want to export.</li>
                <li>Choose the format: PDF (formatted report) or CSV (spreadsheet).</li>
                <li>Select a date range and event types to include.</li>
                <li>Tap "Export" — the file is generated and downloaded to your device.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Privacy During Export</h3>
              <p>All decryption happens on your device. The server never sees your unencrypted data, even during export. The generated file exists only on your device.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">PDF Reports</h3>
              <p>PDF exports include a formatted summary with daily breakdowns, event details, and basic statistics. Great for sharing with your pediatrician during checkups.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">CSV Exports</h3>
              <p>CSV exports provide raw data in a spreadsheet format. Import into Excel, Google Sheets, or any data analysis tool for custom analysis.</p>
            </div>
          </section>

          {/* Privacy & Security */}
          <section id="privacy-security" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Privacy & Security</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>myTotTot is built with a zero-knowledge architecture. Your data is yours — we can't read it, even if we wanted to.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Zero-Knowledge Encryption</h3>
              <p>When you create an account, a pair of encryption keys is generated <strong>on your device</strong>. Your master key is derived from your password and never leaves your device. All data is encrypted before being sent to the server, and decrypted on your device after being retrieved.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">What the Server Knows</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Your email address (for login and notifications)</li>
                <li>Your name (for display to caregivers)</li>
                <li>Encrypted blobs (the server stores these but cannot read them)</li>
                <li>Metadata like timestamps and event types (needed for sorting/filtering)</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">What the Server Does NOT Know</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Your baby's name, date of birth, or gender</li>
                <li>Event details (feeding amounts, sleep durations, notes)</li>
                <li>Photos (encrypted before upload)</li>
                <li>Your password (hashed, never stored in plaintext)</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Encryption Details</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>AES-256-GCM</strong> — Symmetric encryption for all data at rest</li>
                <li><strong>RSA-4096</strong> — Asymmetric encryption for key exchange between caregivers</li>
                <li><strong>PBKDF2</strong> — Password-based key derivation with high iteration count</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Two-Factor Authentication (2FA)</h3>
              <p>Add an extra layer of security with TOTP-based 2FA. Use any authenticator app (Google Authenticator, Authy, 1Password, etc.) to generate time-based codes.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Session Security</h3>
              <p>Sessions use short-lived access tokens (15 minutes) with longer-lived refresh tokens (7 days). When you change your password, all other sessions are invalidated.</p>
            </div>
          </section>

          {/* Settings */}
          <section id="settings" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Settings</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Account Details</h3>
              <p>View and update your name and email address. Email changes may require re-verification.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Password Changes</h3>
              <p>Change your password from Settings. You'll need your current password to set a new one. When your password changes, your encryption keys are re-encrypted with the new password, and all other sessions are logged out.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Two-Factor Authentication</h3>
              <p>Enable or disable 2FA from Settings. When enabling, scan the QR code with your authenticator app and enter the verification code to confirm.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Push Notifications</h3>
              <p>Enable or disable push notifications. See the Push Notifications section for details.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Data Export</h3>
              <p>Access the data export feature from Settings. See the Data Export section for details.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Account Deletion</h3>
              <p>Permanently delete your account and all associated data. This removes all babies (that you own), events, photos, and shared access. You'll need to enter your password to confirm. This action is <strong>irreversible</strong>.</p>
            </div>
          </section>

          {/* Push Notifications */}
          <section id="push-notifications" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Push Notifications</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Enabling Notifications</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to <strong>Settings</strong>.</li>
                <li>Tap <strong>"Enable Push Notifications"</strong>.</li>
                <li>Your browser will show a permission prompt — tap <strong>"Allow"</strong>.</li>
                <li>Once enabled, you'll see a confirmation message.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">What Triggers Notifications</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Reminders (medication times, appointments, custom reminders)</li>
                <li>Emergency access requests from designated contacts</li>
                <li>Caregiver invitation responses</li>
                <li>Post-feed upright timer completion</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Browser Support</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Firefox</strong> — Full support, works immediately.</li>
                <li><strong>Chrome/Edge/Brave</strong> — Requires HTTPS (works in production). Uses Google's Firebase Cloud Messaging.</li>
                <li><strong>Safari (iOS)</strong> — Supported on iOS 16.4+ when app is installed to home screen.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Troubleshooting</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Make sure notifications are allowed in your browser settings.</li>
                <li>On iOS, the app must be added to your home screen first.</li>
                <li>Check that "Do Not Disturb" mode is off on your device.</li>
                <li>Try disabling and re-enabling notifications in Settings.</li>
              </ul>
            </div>
          </section>

          {/* Offline Mode */}
          <section id="offline-mode" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Offline Mode</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <p>myTotTot works without an internet connection, so you can track events anywhere — even without Wi-Fi or mobile data.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">How It Works</h3>
              <p>The app is a Progressive Web App (PWA) that caches everything needed to work offline. When you go offline:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>You can still log events, view cached data, and use timers.</li>
                <li>New events are encrypted and stored locally in a pending queue.</li>
                <li>When your connection returns, events are automatically synced to the server.</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Sync Indicator</h3>
              <p>When you have pending events waiting to sync, an indicator appears showing the count. Once you're back online, the events sync automatically and the indicator disappears.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">What Works Offline</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Logging new events</li>
                <li>Viewing recently cached events</li>
                <li>Using timers</li>
                <li>Viewing baby profiles (cached)</li>
              </ul>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">What Requires Internet</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Uploading photos</li>
                <li>Inviting caregivers</li>
                <li>Creating share links</li>
                <li>Receiving push notifications</li>
                <li>Exporting data</li>
              </ul>
            </div>
          </section>

          {/* Installing the App */}
          <section id="installing-app" className="card p-6 scroll-mt-24">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Installing the App</h2>
            <div className="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 space-y-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">What is a PWA?</h3>
              <p>myTotTot is a Progressive Web App (PWA). This means it runs in your browser but can be "installed" to look and feel like a native app. No app store download needed — it installs directly from your browser.</p>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Installing on iOS (iPhone/iPad)</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Open <strong>Safari</strong> (must be Safari — Chrome on iOS doesn't support PWA install).</li>
                <li>Navigate to the myTotTot website.</li>
                <li>Tap the <strong>Share</strong> button (square with arrow at the bottom of the screen).</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong>.</li>
                <li>Tap <strong>"Add"</strong> in the top right corner.</li>
                <li>The app icon appears on your home screen.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Installing on Android</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Open <strong>Chrome</strong> (or another Chromium-based browser).</li>
                <li>Navigate to the myTotTot website.</li>
                <li>You may see an <strong>"Install app"</strong> banner — tap it. If not:</li>
                <li>Tap the <strong>three-dot menu</strong> in the top right.</li>
                <li>Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</li>
                <li>Confirm by tapping <strong>"Install"</strong>.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Installing on Desktop (Windows/Mac/Linux)</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Open <strong>Chrome</strong> or <strong>Edge</strong>.</li>
                <li>Navigate to the myTotTot website.</li>
                <li>Look for the <strong>install icon</strong> in the address bar (or use the menu).</li>
                <li>Click <strong>"Install"</strong> to add it as a standalone window.</li>
              </ol>

              <h3 className="text-base font-medium text-gray-900 dark:text-white">Benefits of Installing</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Full-screen experience without browser chrome</li>
                <li>Quick access from your home screen</li>
                <li>Offline support</li>
                <li>Push notifications (especially important on iOS)</li>
                <li>Faster loading — the app is cached on your device</li>
              </ul>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}
